import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import LenderService from '../services/LenderService';
import { hasAuthData } from '../helpers/authStorage';
import TermsOfServiceService from '../services/TermsOfServiceService';

export default function useLenderData(me) {
  const queryClient = useQueryClient();

  const isAuthenticated = hasAuthData();

  const roleWithAccess = me?._roles?.find((role) => role.access?.length);

  let role = roleWithAccess?.access?.[0]?.object_type;

  if (!role && me) {
    role = 'unknown';
  }

  const lenderIds =
    role === 'lender'
      ? me?._roles?.[0]?.access
          ?.filter((access) => access.object_type === 'lender')
          .map((access) => access.object_id) || []
      : [];

  const { data: termsOfService, isFetching } = useQuery({
    queryKey: ['terms_of_service'],
    queryFn: TermsOfServiceService.getTermsOfService,
  });

  const queries = useQueries({
    queries: lenderIds.map((id) => ({
      queryKey: ['lenderFromHook', id],
      queryFn: () => LenderService.getLender(id),
      enabled: !!id && me.status && me?.status !== 'pending',
      refetchOnMount: true,
    })),
    combine: (results) => {
      const data = results.map((result) => {
        return result.data;
      });
      // eslint-disable-next-line no-shadow
      const isFetching = results.some((query) => query.isFetching);
      const isFetched = results.every?.((query) => query.isFetched);
      return {
        data,
        isFetching,
        isFetched,
      };
    },
  });

  const refetch = async () => {
    Promise.allSettled(
      lenderIds.map((id) => queryClient.refetchQueries(['lenderFromHook', id]))
    );
  };

  let isReady = false;

  if (!isAuthenticated) {
    isReady = true;
  } else if (role !== 'lender' && role) {
    isReady = true;
    // User is lender but not activated yet
  } else if (me?.status === 'pending') {
    isReady = true;
  } else if (!queries?.data?.length) {
    isReady = false;
  } else if (!termsOfService) {
    isReady = false;
  } else {
    isReady = queries.isFetched;
  }

  return {
    lenders: (queries.data?.filter((lender) => !!lender) || []).map(
      (lender) => ({
        ...lender,
        shouldAcceptTermsOfService:
          !lender?.accepted_tos_date ||
          !moment(lender?.accepted_tos_date).isSameOrAfter(
            moment(termsOfService?.updated_date)
          ),
      })
    ),
    isFetching: queries.isFetching || isFetching,
    isReady,
    refetch,
    unOnboarded: queries.data?.some((lender) => {
      return !lender?.onboarded;
    }),
    shouldAcceptTermsOfService: queries.data?.some((lender) => {
      return (
        !lender?.accepted_tos_date ||
        !moment(lender?.accepted_tos_date).isSameOrAfter(
          moment(termsOfService?.updated_date)
        )
      );
    }),
  };
}
